/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Mikhail Kokoulin
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */
import { Component } from 'react';

import includes from 'lodash/includes';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import { PersonalAccountField } from '@/main/types/personalAccountTypes';
import {
    IPageTitleSettingButtonProps,
    renderDropFilterButton,
    renderSettingButton,
} from '@components/common/PageTitle/PageTitle';
import { ITableField, TableFieldType } from '@components/Table/reducers/tableReducer';
import { TableNameType } from '@components/Table/types/tableTypes';
import { EXPORT_ACCOUNT } from '@modules/app/Api';
import { ICommonTranslation , IPagePersonalAccountTranslation } from '@modules/internationalization/types';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { ONBOARDING_DATA_ATTR } from '@modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '@modules/onboarding/types/onboardingTypes';

import { icons } from '../../../../assets';
import { PageTitle } from '../../../../components/common';
import TableSetting from '../../../../components/TableSetting/TableSetting';
import DensityButton from '../../../../modules/density/components/DensityButton';
import ExportButton from '../../../../modules/exports/components/ExportButton';


interface IPersonalAccountTitleProps {
    counter: number;
    dataName?: string;
    isFilterSelected: boolean;
    onDropFiltersClick: () => void;
    openModal: (payload: IModalOpenPayload) => void;
    sendTableFields: (tableFields: Array<ITableField<PersonalAccountField>>) => void;
    tableFields: Array<ITableField<PersonalAccountField>>;
    translation: IPagePersonalAccountTranslation;
    translationCommon: ICommonTranslation;
}

class PersonalAccountTitle extends Component<IPersonalAccountTitleProps> {

    onOpenTableSetting = () => {
        const {
            tableFields,
            translation,
        } = this.props;

        this.props.openModal({
            render: (props: IModalRenderProps) => <TableSetting
                fields={this.filterFields(tableFields, ['S', 'M'])}
                isOpen={false}
                locale={({
                    title: translation.title.tableSetting,
                    table: translation.table,
                })}
                requiredFields={this.filterFields(tableFields, ['C'])}
                onApply={(tableFields: Array<ITableField<PersonalAccountField>>) => {
                    this.onSendTableFields(tableFields);
                    props.onCloseModal();
                }}
                onClose={props.onCloseModal}
            />,
        });
    };

    onSendTableFields = (tableFields: Array<ITableField<PersonalAccountField>>) => {
        this.props.sendTableFields(tableFields);
    };

    render() {
        const {
            counter,
            isFilterSelected,
            onDropFiltersClick,
            translation,
            translationCommon,
        } = this.props;

        const isFastActionPage = getIsFastActionPage();
        const buttonsIdsForFastAction = [0,4];
        const { instructionsLinks: { lcGeneral } } = translationCommon;

        return (
            <PageTitle
                count={counter}
                dataName="personalAccount"
                instructionLink={lcGeneral}
                title={translation.title.header}
            >
                {
                    // render drop filter button
                    isFilterSelected && renderDropFilterButton({
                        onClick: onDropFiltersClick,
                        value: translation.title.dropFilters,
                    })
                }
                {
                    // render table setting buttons in right side of title
                    this.tableSettingButtons().map(button => {
                        if (button.component ) {
                            if (isFastActionPage) {
                                return includes(buttonsIdsForFastAction, button.id) ?
                                    <div
                                        className="page-title__button"
                                        key={button.id}
                                        {...{ [ONBOARDING_DATA_ATTR]: button.onboarding }}
                                    >
                                        {button.component}
                                    </div> : <></>;

                            }
                            return <div
                                className="page-title__button"
                                key={button.id}
                                {...{ [ONBOARDING_DATA_ATTR]: button.onboarding }}
                            >
                                {button.component}
                            </div>;
                        }
                        return renderSettingButton(button);
                    })
                }
            </PageTitle>
        );
    }

    /**
     * @description Filter table fields according to field type.
     * @param {ITableField<PersonalAccountField>[]} tableFields
     * @param {TableFieldType[]} type
     * @return {ITableField<PersonalAccountField>[]}
     */
    private filterFields = (tableFields: Array<ITableField<PersonalAccountField>>, type: TableFieldType[]) => (
        tableFields.filter((value: ITableField<PersonalAccountField>) => type.includes(value.type))
    );

    /**
     * @description Return array of table setting buttons
     * @return {IPageTitleSettingButtonProps[]}
     */
    private tableSettingButtons = (): IPageTitleSettingButtonProps[] =>
        [
            {
                id: 0,
                dataName: 'Settings of columns',
                iconLeft: icons.settingIcon,
                onboarding: EOnboardingComponentsId.accountsTableSettings,
                onClick: this.onOpenTableSetting,
                value: this.props.translation.title.tableSetting.titleBtn,
            },
            {
                id: 1,
                component: <ExportButton
                    pageIns
                    instructionLink={this.props.translationCommon.instructionsLinks.lcExport}
                    src={EXPORT_ACCOUNT}
                    tableName={TableNameType.account}
                />,
            },
            {
                id: 4,
                component: <DensityButton page={ECommonTranslationPages.personalAccount}/>,
            },
        ];
}

export default PersonalAccountTitle;
